/* Onboarding: first-run question, coach marks, help panel (W2-14). Design tokens only (src/ui/styles/tokens.css). */

.muted {
  margin: 0;
  color: var(--color-text-muted);
  font-size: var(--text-sm);
}

.spacer {
  flex: 1;
}

/* Read by screen readers only (live announcements). */
.srOnly {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip-path: inset(50%);
  border: 0;
  white-space: nowrap;
}

/* ---------- first-run question ---------- */

.dialogFooter {
  display: flex;
  align-items: center;
  gap: var(--space-4);
  width: 100%;
  justify-content: space-between;
}

.choices {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.choice {
  display: flex;
  align-items: center;
  gap: var(--space-4);
  width: 100%;
  padding: var(--space-3) var(--space-4);
  border: var(--border-width) solid var(--color-border);
  border-radius: var(--radius-lg);
  background: var(--color-surface-2);
  color: var(--color-text);
  font: inherit;
  text-align: left;
  cursor: pointer;
}

.choice:hover {
  background: var(--color-surface-hover);
}

.choice:focus-visible {
  outline: var(--focus-ring-width) solid var(--color-focus-ring);
  outline-offset: var(--focus-ring-offset);
}

.choice[data-checked='true'] {
  border-color: var(--color-accent);
  background: var(--color-accent-muted);
}

.choiceIcon {
  display: inline-flex;
  color: var(--color-accent);
}

.choiceText {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--space-0);
}

.choiceTitle {
  font-weight: var(--weight-semibold);
}

.choiceBody {
  color: var(--color-text-muted);
  font-size: var(--text-sm);
}

.choiceMark {
  display: inline-flex;
  color: var(--color-text-subtle);
}

.choice[data-checked='true'] .choiceMark {
  color: var(--color-accent);
}

.languageRow {
  display: flex;
  align-items: flex-end;
  gap: var(--space-3);
  margin-top: var(--space-4);
  color: var(--color-text-muted);
}

/* ---------- coach marks ---------- */

.coachLayer {
  position: fixed;
  inset: 0;
  z-index: var(--z-popover);
  pointer-events: none;
}

.coachRing {
  position: fixed;
  border: 2px solid var(--color-accent);
  border-radius: var(--radius-lg);
  box-shadow:
    0 0 0 4px var(--color-accent-muted),
    var(--shadow-md);
  pointer-events: none;
  transition:
    left var(--duration-normal) var(--ease-standard),
    top var(--duration-normal) var(--ease-standard),
    width var(--duration-normal) var(--ease-standard),
    height var(--duration-normal) var(--ease-standard);
  animation: ringPulse 1.6s var(--ease-standard) infinite;
}

.coachLayer[data-reduced-motion='true'] .coachRing {
  animation: none;
  transition: none;
}

@keyframes ringPulse {
  0%,
  100% {
    box-shadow:
      0 0 0 4px var(--color-accent-muted),
      var(--shadow-md);
  }
  50% {
    box-shadow:
      0 0 0 10px var(--color-accent-muted),
      var(--shadow-md);
  }
}

.coachCard {
  position: fixed;
  left: 50%;
  top: 50%;
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  width: min(calc(340px * var(--ui-scale)), calc(100vw - 16px));
  padding: var(--space-4);
  border: var(--border-width) solid var(--color-accent-border);
  border-radius: var(--radius-lg);
  background: var(--color-surface-overlay);
  color: var(--color-text);
  box-shadow: var(--shadow-lg);
  pointer-events: auto;
}

.coachCard:focus-visible {
  outline: var(--focus-ring-width) solid var(--color-focus-ring);
  outline-offset: var(--focus-ring-offset);
}

/*
 * Docked chip (round-2 UX): what the tour shows while Theater or test play covers the window, or the step's part
 * is in another workspace. Small, at the bottom left (clear of the Publish export bar at the bottom right and of
 * the Theater stage), and it makes no claim about the target "appearing".
 */
.coachChip {
  position: fixed;
  left: 8px;
  bottom: 8px;
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  max-width: calc(100vw - 16px);
  padding: var(--space-1) var(--space-2);
  border: var(--border-width) solid var(--color-accent-border);
  border-radius: var(--radius-pill);
  background: var(--color-surface-overlay);
  color: var(--color-text);
  box-shadow: var(--shadow-md);
  pointer-events: auto;
}

.coachChip:focus-visible {
  outline: var(--focus-ring-width) solid var(--color-focus-ring);
  outline-offset: var(--focus-ring-offset);
}

.coachChipLabel {
  font-size: var(--text-xs);
  color: var(--color-text-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.coachHeader {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-2);
}

.coachLabel {
  color: var(--color-text-muted);
  font-size: var(--text-xs);
  font-weight: var(--weight-medium);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.coachTitle {
  margin: 0;
  font-size: var(--text-lg);
  font-weight: var(--weight-semibold);
}

.coachBody {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.coachText {
  margin: 0;
  line-height: var(--leading-normal);
}

.coachDone,
.coachHint {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  margin: 0;
  font-size: var(--text-sm);
}

.coachDone > svg {
  color: var(--color-success);
}

.coachHint {
  color: var(--color-text-muted);
}

.coachDots {
  display: flex;
  gap: var(--space-1);
  margin: 0;
  padding: 0;
  list-style: none;
}

.coachDots > li {
  width: 8px;
  height: 8px;
  border: var(--border-width) solid var(--color-border-strong);
  border-radius: var(--radius-pill);
}

.coachDots > li[data-state='done'] {
  border-color: var(--color-success);
  background: var(--color-success);
}

.coachDots > li[data-state='current'] {
  width: 18px;
  border-color: var(--color-accent);
  background: var(--color-accent);
}

.coachFooter {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  margin-top: var(--space-1);
}

/* ---------- help panel ---------- */

.helpPanel {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.helpFooter {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
}

.topicList {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  margin: 0;
  padding: 0;
  list-style: none;
}

.topicButton {
  display: flex;
  flex-direction: column;
  gap: var(--space-0);
  width: 100%;
  padding: var(--space-2) var(--space-3);
  border: var(--border-width) solid transparent;
  border-radius: var(--radius-md);
  background: transparent;
  color: var(--color-text);
  font: inherit;
  text-align: left;
  cursor: pointer;
}

.topicButton:hover {
  background: var(--color-surface-hover);
}

.topicButton:focus-visible {
  outline: var(--focus-ring-width) solid var(--color-focus-ring);
  outline-offset: var(--focus-ring-offset);
}

.topicTitle {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  font-weight: var(--weight-semibold);
}

.topicExcerpt {
  overflow: hidden;
  color: var(--color-text-muted);
  font-size: var(--text-sm);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.helpArticle {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--space-3);
}

.articleTitle {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  margin: 0;
  font-size: var(--text-xl);
}

.article {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  line-height: var(--leading-normal);
}

.article p,
.article ul,
.article ol,
.article h4 {
  margin: 0;
}

.article ul,
.article ol {
  padding-left: var(--space-5);
}

.keyRef {
  white-space: nowrap;
}

.article code {
  padding: 0 var(--space-1);
  border-radius: var(--radius-xs);
  background: var(--color-surface-3);
  font-family: var(--font-mono);
  font-size: 0.92em;
}

.tableWrap {
  max-width: 100%;
  overflow-x: auto;
}

.article table {
  border-collapse: collapse;
  font-size: var(--text-sm);
}

.article th,
.article td {
  padding: var(--space-1) var(--space-3);
  border-bottom: var(--border-width) solid var(--color-divider);
  text-align: left;
  vertical-align: top;
}

.article th {
  color: var(--color-text-muted);
  font-weight: var(--weight-semibold);
}

.keymapBox {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--space-2);
  width: 100%;
  padding: var(--space-3);
  border-radius: var(--radius-lg);
  background: var(--color-surface-2);
}
